import { useEffect, useState } from "react";
import { Wand2, Loader2, X, Link2, Plus, Sparkles } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { useToast } from "@/hooks/use-toast";
import { cn } from "@/lib/utils";
import { OUTFIT_TEMPLATES, type OutfitTemplate } from "@/lib/outfit-templates";

// Re-export so existing imports from this module keep working.
export { OUTFIT_TEMPLATES, type OutfitTemplate };

export interface CustomizeData {
  prompt: string;
  referenceLinks: string[];
  referenceFiles: File[];
}

interface Props {
  busy: boolean;
  buttonLabel?: string;
  /** Pre-fill the prompt (used when launching from a template tile). */
  initialPrompt?: string;
  /** Open state controlled from the outside (optional). */
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
  /** When true, hide the trigger button — the dialog is opened via `open` prop. */
  hideTrigger?: boolean;
  onGenerate: (data: CustomizeData) => void | Promise<void>;
}

export const CustomizeOutfitDialog = ({
  busy,
  buttonLabel = "Generate full outfit",
  initialPrompt,
  open: openProp,
  onOpenChange,
  hideTrigger,
  onGenerate,
}: Props) => {
  const { toast } = useToast();
  const [internalOpen, setInternalOpen] = useState(false);
  const open = openProp ?? internalOpen;
  const setOpen = (v: boolean) => {
    if (onOpenChange) onOpenChange(v);
    else setInternalOpen(v);
  };
  const [prompt, setPrompt] = useState(initialPrompt ?? "");
  const [selectedTemplate, setSelectedTemplate] = useState<string | null>(null);

  // When opened with an initialPrompt (e.g. from a template card), seed the textarea.
  useEffect(() => {
    if (open && initialPrompt !== undefined) setPrompt(initialPrompt);
  }, [open, initialPrompt]);
  const [linkInput, setLinkInput] = useState("");
  const [links, setLinks] = useState<string[]>([]);

  const pickTemplate = (t: OutfitTemplate) => {
    if (selectedTemplate === t.id) {
      setSelectedTemplate(null);
      setPrompt("");
    } else {
      setSelectedTemplate(t.id);
      setPrompt(t.prompt);
    }
  };

  const addLink = () => {
    const v = linkInput.trim();
    if (!v) return;
    try {
      new URL(v);
    } catch {
      toast({ title: "Invalid URL", variant: "destructive" });
      return;
    }
    if (links.length >= 3) {
      toast({ title: "Up to 3 links" });
      return;
    }
    setLinks([...links, v]);
    setLinkInput("");
  };

  const removeLink = (i: number) => setLinks(links.filter((_, idx) => idx !== i));

  const submit = async () => {
    // Close the dialog FIRST so the full-screen GenerationOverlay (rendered by
    // the parent page) becomes visible — otherwise this dialog sits on top of
    // it and the user sees nothing happening.
    const payload = { prompt: prompt.trim(), referenceLinks: links, referenceFiles: [] };
    setOpen(false);
    setSelectedTemplate(null);
    await onGenerate(payload);
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      {!hideTrigger && (
        <DialogTrigger asChild>
          <Button variant="outline" className="h-12 w-full" disabled={busy}>
            {busy ? (
              <><Loader2 className="mr-2 h-4 w-4 animate-spin" /> Styling full look…</>
            ) : (
              <><Wand2 className="mr-2 h-4 w-4" /> {buttonLabel}</>
            )}
          </Button>
        </DialogTrigger>
      )}
      <DialogContent className="max-h-[90vh] overflow-y-auto sm:max-w-md">
        <DialogHeader>
          <DialogTitle>Customize your outfit</DialogTitle>
        </DialogHeader>

        <div className="space-y-4">
          <div>
            <Label className="flex items-center gap-1.5 text-sm font-semibold">
              <Sparkles className="h-3.5 w-3.5 text-primary" /> Style templates
            </Label>
            <p className="mb-2 mt-0.5 text-xs text-muted-foreground">
              One-tap looks. Tap again to clear.
            </p>
            <div className="flex flex-wrap gap-1.5">
              {OUTFIT_TEMPLATES.map((t) => (
                <button
                  key={t.id}
                  type="button"
                  onClick={() => pickTemplate(t)}
                  className={cn(
                    "rounded-full border px-3 py-1.5 text-xs font-medium transition-all",
                    selectedTemplate === t.id
                      ? "border-primary bg-primary text-primary-foreground shadow-sm"
                      : "border-border bg-background hover:border-primary/50 hover:bg-accent",
                  )}
                >
                  <span className="mr-1">{t.emoji}</span>
                  {t.label}
                </button>
              ))}
            </div>

            {/* Visual preview of the selected template. Lets users see the
                style reference before generating. The generation prompt
                preserves their face & body — only the vibe comes from this. */}
            {selectedTemplate && (() => {
              const t = OUTFIT_TEMPLATES.find((x) => x.id === selectedTemplate);
              if (!t) return null;
              return (
                <div className="mt-3 animate-fade-in overflow-hidden rounded-xl border border-primary/30 bg-card shadow-soft">
                  <div className="relative aspect-[4/3] w-full bg-muted">
                    <img
                      src={t.image}
                      alt={`${t.label} style reference`}
                      className="absolute inset-0 h-full w-full object-cover"
                    />
                    <div className="absolute left-2 top-2 rounded-full bg-black/70 px-2.5 py-1 text-[10px] font-semibold uppercase tracking-wide text-white">
                      <span className="mr-1">{t.emoji}</span>
                      Preview · {t.label}
                    </div>
                  </div>
                  <p className="px-3 py-2 text-[11px] text-muted-foreground">
                    Your face &amp; body stay yours — this is just the style reference.
                  </p>
                </div>
              );
            })()}
          </div>

          <div>
            <Label htmlFor="customize-prompt" className="text-sm font-semibold">Style prompt (optional)</Label>
            <p className="mb-2 mt-0.5 text-xs text-muted-foreground">
              Fine-tune the vibe — overrides template.
            </p>
            <Textarea
              id="customize-prompt"
              name="customizePrompt"
              autoComplete="off"
              value={prompt}
              onChange={(e) => {
                setPrompt(e.target.value);
                setSelectedTemplate(null);
              }}
              placeholder="e.g. streetwear with sneakers, neutral colors, going to a beach party…"
              maxLength={500}
              rows={3}
            />
          </div>

          <div>
            <Label htmlFor="customize-link" className="text-sm font-semibold">Reference links (optional)</Label>
            <p className="mb-2 mt-0.5 text-xs text-muted-foreground">
              Paste e-commerce product links for inspiration.
            </p>
            <div className="flex gap-2">
              <div className="relative flex-1">
                <Link2 className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
                <Input
                  id="customize-link"
                  name="customizeLink"
                  autoComplete="url"
                  type="url"
                  value={linkInput}
                  onChange={(e) => setLinkInput(e.target.value)}
                  placeholder="https://…"
                  className="pl-9"
                  onKeyDown={(e) => {
                    if (e.key === "Enter") {
                      e.preventDefault();
                      addLink();
                    }
                  }}
                />
              </div>
              <Button type="button" variant="outline" size="icon" onClick={addLink}>
                <Plus className="h-4 w-4" />
              </Button>
            </div>
            {links.length > 0 && (
              <ul className="mt-2 space-y-1">
                {links.map((l, i) => (
                  <li key={i} className="flex items-center gap-2 rounded-md bg-muted px-2 py-1 text-xs">
                    <Link2 className="h-3 w-3 shrink-0 text-muted-foreground" />
                    <span className="flex-1 truncate">{l}</span>
                    <button type="button" onClick={() => removeLink(i)} aria-label="Remove link">
                      <X className="h-3 w-3" />
                    </button>
                  </li>
                ))}
              </ul>
            )}
          </div>
          <div className="space-y-1.5 pt-1">
            <Button
              className="h-12 w-full gradient-primary text-primary-foreground"
              onClick={submit}
              disabled={busy}
            >
              <Wand2 className="mr-2 h-4 w-4" /> Generate (1 credit)
            </Button>
            <p className="text-center text-[11px] text-muted-foreground">
              Usually 30–60s · Style Score after is free · Refunded if it fails
            </p>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};
